import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, CheckCircle2, ArrowRight, Send, Trash2 } from 'lucide-react';
import { formatINR, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextField, TextAreaField } from '../../components/ui/Field.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';

const STATUS_LABEL = {
  draft: 'Draft',
  received: 'Received',
  in_progress: 'In progress',
  ready: 'Ready for pickup',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_TONE = {
  draft: 'border-warning text-warning',
  received: 'border-muted text-muted',
  in_progress: 'border-warning text-warning',
  ready: 'border-primary text-primary',
  delivered: 'border-success text-success',
  cancelled: 'border-danger text-danger',
};

const NEXT_STEP = { received: 'in_progress', in_progress: 'ready', ready: 'delivered' };

export default function RepairViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [advanceOpen, setAdvanceOpen] = useState(false); // simple in_progress/ready step confirm
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [finalCharge, setFinalCharge] = useState('');
  const [deliveredTo, setDeliveredTo] = useState('');
  const [reason, setReason] = useState('');
  const [statusError, setStatusError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { data: voucher, isLoading } = useQuery({
    queryKey: ['repairs', id],
    queryFn: () => api.get(`/repairs/${id}`),
  });

  const statusMutation = useMutation({
    mutationFn: (body) => api.post(`/repairs/${id}/status`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      setAdvanceOpen(false);
      setDeliverOpen(false);
      setCancelOpen(false);
      setReason('');
    },
    onError: (err) => setStatusError(err instanceof ApiClientError ? err.message : 'Could not update this voucher.'),
  });

  const issueMutation = useMutation({
    mutationFn: () => api.post(`/repairs/${id}/issue`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['repairs'] }),
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not issue this voucher.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/repairs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      navigate('/repairs');
    },
    onError: (err) => setActionError(err instanceof ApiClientError ? err.message : 'Could not delete this draft.'),
  });

  if (isLoading || !voucher) return null;

  const nextStep = NEXT_STEP[voucher.status];
  const canCancel = !['draft', 'delivered', 'cancelled'].includes(voucher.status);

  const advanceLabel = { in_progress: 'Start work', ready: 'Mark ready', delivered: 'Deliver' }[nextStep];

  const openStep = () => {
    setStatusError(null);
    if (nextStep === 'delivered') {
      setFinalCharge(voucher.estimated_charge);
      setDeliverOpen(true);
    } else {
      setAdvanceOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/repairs" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{voucher.voucher_no ?? 'Draft voucher'}</h1>
          <p className="text-sm text-muted">{new Date(voucher.voucher_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        <span className={`ml-auto rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[voucher.status]}`}>
          {STATUS_LABEL[voucher.status]}
        </span>
      </div>

      {voucher.status === 'draft' ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          This voucher hasn&rsquo;t been issued yet — it has no voucher number and the items haven&rsquo;t been logged
          as received. Issue it to finalise, or delete it if it was a mistake.
        </p>
      ) : null}

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Customer</h2>
          <p className="font-medium">{voucher.customer_name}</p>
          {voucher.customer_mobile ? <p className="text-sm text-muted">{voucher.customer_mobile}</p> : null}
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Delivery</h2>
          <p className="font-medium">{voucher.expected_delivery_date ? new Date(voucher.expected_delivery_date).toLocaleDateString('en-IN') : 'Not specified'}</p>
          {voucher.karigar_name ? <p className="text-sm text-muted">Aasari: {voucher.karigar_name}</p> : null}
          {voucher.status === 'delivered' ? (
            <p className="text-sm text-success">Delivered {new Date(voucher.actual_delivery_date).toLocaleDateString('en-IN')} to {voucher.delivered_to}</p>
          ) : null}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3 text-right">Weight</th>
                <th className="py-2">Repair required</th>
              </tr>
            </thead>
            <tbody>
              {voucher.items.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 font-medium">{item.item_name}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatWeight(item.weight)}</td>
                  <td className="py-2">
                    {item.repair_details}
                    {item.remarks ? <span className="text-muted"> · {item.remarks}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Total weight</dt><dd className="tabular">{formatWeight(voucher.total_weight)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Estimated charge</dt><dd className="tabular">{formatINR(voucher.estimated_charge)}</dd></div>
          {Number(voucher.advance_amount) > 0 ? <div className="flex justify-between"><dt className="text-muted">Advance paid</dt><dd className="tabular">{formatINR(voucher.advance_amount)}</dd></div> : null}
          {voucher.status === 'delivered' ? <div className="flex justify-between font-semibold text-primary"><dt>Final charge</dt><dd className="tabular">{formatINR(voucher.final_charge)}</dd></div> : null}
        </dl>
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Status history</h2>
          <ol className="flex flex-col gap-2 text-sm">
            {voucher.statusHistory.map((h) => (
              <li key={h.id} className="flex justify-between gap-2">
                <span>{STATUS_LABEL[h.to_status]}{h.notes ? <span className="text-muted"> — {h.notes}</span> : null}</span>
                <span className="shrink-0 text-muted">{new Date(h.changed_at).toLocaleDateString('en-IN')}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {actionError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        {voucher.status === 'draft' ? (
          <>
            <Button
              onClick={() => { setActionError(null); issueMutation.mutate(); }}
              loading={issueMutation.isPending}
              className="flex-1 sm:flex-none"
            >
              <Send size={17} aria-hidden="true" /> Issue voucher
            </Button>
            <Button
              variant="danger"
              onClick={() => { setActionError(null); setDeleteOpen(true); }}
              className="flex-1 sm:flex-none"
            >
              <Trash2 size={17} aria-hidden="true" /> Delete draft
            </Button>
          </>
        ) : (
          <>
            <PdfActions
              path={`/repairs/${id}/pdf`}
              documentLabel={`Repair voucher ${voucher.voucher_no}`}
              fileBaseName={voucher.voucher_no}
              onError={setActionError}
            />
            <ThermalPrintButton printPath={`/repairs/${id}`} />
            {nextStep ? (
              <Button onClick={openStep} className="flex-1 sm:flex-none">
                <ArrowRight size={17} aria-hidden="true" /> {advanceLabel}
              </Button>
            ) : null}
            {canCancel ? (
              <Button variant="danger" onClick={() => { setStatusError(null); setCancelOpen(true); }} className="flex-1 sm:flex-none">
                <Ban size={17} aria-hidden="true" /> Cancel
              </Button>
            ) : null}
          </>
        )}
      </div>

      <Modal open={advanceOpen} onClose={() => setAdvanceOpen(false)} title={`Move to "${STATUS_LABEL[nextStep]}"?`}>
        <div className="flex flex-col gap-4">
          {statusError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{statusError}</p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setAdvanceOpen(false)}>Not yet</Button>
            <Button loading={statusMutation.isPending} onClick={() => statusMutation.mutate({ toStatus: nextStep })}>
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deliverOpen} onClose={() => setDeliverOpen(false)} title="Deliver to customer">
        <div className="flex flex-col gap-4">
          <TextField label="Final charge (₹)" type="number" inputMode="decimal" value={finalCharge} onChange={(e) => setFinalCharge(e.target.value)} />
          <TextField label="Delivered to" required value={deliveredTo} onChange={(e) => setDeliveredTo(e.target.value)} />
          {statusError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{statusError}</p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeliverOpen(false)}>Cancel</Button>
            <Button
              disabled={!deliveredTo.trim()}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate({ toStatus: 'delivered', finalCharge: Number(finalCharge), deliveredTo })}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm delivery
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this voucher?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The voucher number <b>{voucher.voucher_no}</b> stays reserved and is never reused. If items were already
            logged as received, this records that custody returns to the customer.
          </p>
          <TextAreaField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          {statusError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{statusError}</p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>Keep voucher</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={statusMutation.isPending}
              onClick={() => statusMutation.mutate({ toStatus: 'cancelled', notes: reason })}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete this draft?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            This draft never took a voucher number and the items were never logged as received, so deleting it leaves
            no trace and no gap in the series. This can&rsquo;t be undone.
          </p>
          {actionError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{actionError}</p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteOpen(false)}>Keep draft</Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              <Trash2 size={16} aria-hidden="true" /> Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
